import { NavLink } from 'react-router'
import { useCurrentUser } from '../auth/AuthProvider.tsx'
import { cn } from '../lib/cn.ts'
import { hint } from '../lib/hint.ts'
import { COMING_SOON, joinPath, visibleModules } from '../modules/registry.ts'

export function SideNav({ collapsed }: { collapsed: boolean }) {
  const user = useCurrentUser()
  const modules = visibleModules(user)
  const tip = (label: string) => (collapsed ? hint(label, 'right') : {})

  const rowBase = cn(
    'relative flex h-9 items-center gap-3 rounded-ctl text-sm whitespace-nowrap',
    collapsed ? 'justify-center px-0' : 'px-2.5',
  )

  return (
    <nav aria-label="Modules" data-tour="module-nav" className="flex flex-col gap-4 p-2.5">
      {modules.map((m) => (
        <section key={m.id} aria-labelledby={`nav-${m.id}`}>
          <div
            className={cn('mb-1 flex items-center gap-2', collapsed ? 'justify-center' : 'px-2.5')}
            {...tip(m.name)}
          >
            <span
              data-color={m.color}
              aria-hidden
              className="flex size-6 shrink-0 items-center justify-center rounded-card bg-cb text-sm text-cf"
            >
              <m.icon />
            </span>
            <h2
              id={`nav-${m.id}`}
              className={cn(
                'text-xs font-semibold tracking-wide whitespace-nowrap text-faint uppercase',
                collapsed && 'sr-only',
              )}
            >
              {m.name}
            </h2>
          </div>
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {m.visibleItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={joinPath(m.basePath, item.path)}
                  className={({ isActive }) =>
                    cn(
                      rowBase,
                      'no-underline transition-colors',
                      isActive
                        ? 'bg-surface-2 font-semibold text-text before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-accent'
                        : 'font-medium text-muted hover:bg-surface-2 hover:text-text',
                    )
                  }
                  {...tip(item.label)}
                >
                  <span
                    data-color={item.color ?? m.color}
                    aria-hidden
                    className="flex shrink-0 text-lg text-cf"
                  >
                    <item.icon />
                  </span>
                  <span className={cn(collapsed && 'sr-only')}>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <div className="border-t border-border pt-3">
        {COMING_SOON.map((c) => (
          // aria-disabled (not `disabled`) keeps it focusable and hoverable so the hint shows.
          <button
            type="button"
            key={c.id}
            aria-disabled="true"
            className={cn(
              rowBase,
              'w-full cursor-not-allowed border-0 bg-transparent font-medium text-faint opacity-70',
            )}
            {...hint(collapsed ? `${c.name}: ${c.hint}` : c.hint, 'right')}
          >
            <span aria-hidden className="flex shrink-0 text-lg">
              <c.icon />
            </span>
            <span className={cn(collapsed && 'sr-only')}>{c.name}</span>
            {!collapsed && (
              <span className="ml-auto rounded-full bg-surface-2 px-2 text-[11px] font-semibold text-faint">
                Soon
              </span>
            )}
          </button>
        ))}
      </div>
    </nav>
  )
}
